// The one-page handout for an appointment (brief §23).
//
// Printed from the browser rather than generated as a PDF server-side — print-to-PDF
// produces the same artefact and keeps a PDF toolchain out of the deployment.
// Nothing on this page is AI-written: every figure comes from the analytics layer,
// and the questions are the user's own.

import { useEffect, useState } from 'react';
import { api, type DoctorSummary as Summary } from '../lib/api.ts';
import { Button, Card, ErrorNote, SectionTitle, Spinner, TextArea } from '../components/ui.tsx';
import { longDate, titleCase, CONFOUNDER_LABELS } from '../lib/format.ts';

export function DoctorSummary({ id }: { id: string }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [questions, setQuestions] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.settings.doctorSummary(id, [])
      .then((r) => setSummary(r.summary))
      .catch((e) => setError((e as Error).message));
  }, [id]);

  if (error) return <ErrorNote message={error} />;
  if (!summary) return <Spinner />;

  const questionList = questions.split('\n').map((q) => q.trim()).filter(Boolean);

  return (
    <div className="space-y-5">
      <div className="no-print space-y-3">
        <p className="text-sm text-ink-2">
          A short summary you can print or show on your phone. Add anything you want to ask.
        </p>
        <TextArea value={questions} onChange={setQuestions} rows={3}
          placeholder={'Questions for your doctor, one per line'} />
        <Button variant="primary" full onClick={() => window.print()}>Print / save as PDF</Button>
      </div>

      <article className="space-y-5">
        <header>
          <h1 className="text-lg font-semibold">{summary.experiment_name.replace(/^\[demo\]\s*/, '')}</h1>
          <p className="text-sm text-ink-2 mt-1">{summary.problem}</p>
          <p className="text-xs text-muted mt-1">
            Self-tracked · generated {longDate(summary.generated_at.slice(0, 10))}
          </p>
        </header>

        <section>
          <SectionTitle>Dates</SectionTitle>
          <Card className="text-sm space-y-1">
            {summary.dates.baseline && <p><span className="text-muted">Baseline: </span>{summary.dates.baseline}</p>}
            {summary.dates.intervention && <p><span className="text-muted">Treatment: </span>{summary.dates.intervention}</p>}
          </Card>
        </section>

        <section>
          <SectionTitle>What was done</SectionTitle>
          <Card>
            <ul className="text-sm space-y-2">
              {summary.interventions.map((iv, i) => (
                <li key={i}>
                  <p className="font-medium">{iv.name}</p>
                  <p className="text-xs text-ink-2">
                    {iv.schedule}{iv.dose ? ` · ${iv.dose}` : ''}
                  </p>
                </li>
              ))}
            </ul>
            <p className="text-sm mt-3 pt-3 border-t border-hairline">
              Adherence: {summary.adherence.percent === null
                ? 'not applicable'
                : `${summary.adherence.completed} of ${summary.adherence.expected} scheduled (${summary.adherence.percent}%)`}
            </p>
          </Card>
        </section>

        <section>
          <SectionTitle>Measures</SectionTitle>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-hairline">
                  <th scope="col" className="py-1.5 pr-2 font-medium">Measure</th>
                  <th scope="col" className="py-1.5 px-2 text-right font-medium">Baseline</th>
                  <th scope="col" className="py-1.5 px-2 text-right font-medium">Treatment</th>
                  <th scope="col" className="py-1.5 pl-2 text-right font-medium">Change</th>
                </tr>
              </thead>
              <tbody>
                {summary.metrics.filter((m) => m.baseline_average !== null || m.intervention_average !== null).map((m) => (
                  <tr key={m.name} className="border-b border-hairline last:border-0">
                    <td className="py-1.5 pr-2">{m.name}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{m.baseline_average ?? '—'}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{m.intervention_average ?? '—'}</td>
                    <td className="py-1.5 pl-2 text-right tabular-nums">
                      {m.change_percent === null ? '—' : `${m.change_percent > 0 ? '+' : ''}${m.change_percent}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </section>

        {summary.observations.length > 0 && (
          <section>
            <SectionTitle>Notable</SectionTitle>
            <Card>
              <ul className="text-sm space-y-1">
                {summary.observations.map((o, i) => <li key={i}>· {o}</li>)}
              </ul>
            </Card>
          </section>
        )}

        {(summary.side_effects.length > 0 || summary.confounders.length > 0) && (
          <section>
            <SectionTitle>Side effects and other factors</SectionTitle>
            <Card className="text-sm space-y-1">
              {summary.side_effects.length > 0 && (
                <p><span className="text-muted">Side effects: </span>{summary.side_effects.join(', ')}</p>
              )}
              {summary.confounders.length > 0 && (
                <p><span className="text-muted">Other factors: </span>
                  {summary.confounders.map((c) => CONFOUNDER_LABELS[c] ?? titleCase(c)).join(', ')}</p>
              )}
            </Card>
          </section>
        )}

        {summary.current_medications.length > 0 && (
          <section>
            <SectionTitle>Current medications</SectionTitle>
            <Card>
              <ul className="text-sm space-y-1">
                {summary.current_medications.map((m, i) => (
                  <li key={i}>
                    {m.name.replace(/^\[demo\]\s*/, '')}
                    {m.strength && ` ${m.strength}`}
                    {m.dose && ` · ${m.dose}`}
                    {m.frequency && ` · ${m.frequency}`}
                  </li>
                ))}
              </ul>
            </Card>
          </section>
        )}

        {summary.allergies.length > 0 && (
          <section>
            <SectionTitle>Allergies</SectionTitle>
            <Card>
              <ul className="text-sm space-y-1">
                {summary.allergies.map((a, i) => (
                  <li key={i}>{a.substance}{a.reaction && ` — ${a.reaction}`}</li>
                ))}
              </ul>
            </Card>
          </section>
        )}

        {questionList.length > 0 && (
          <section>
            <SectionTitle>Questions</SectionTitle>
            <Card>
              <ul className="text-sm space-y-1.5">
                {questionList.map((q, i) => <li key={i}>{i + 1}. {q}</li>)}
              </ul>
            </Card>
          </section>
        )}

        <p className="text-xs text-muted leading-relaxed border-t border-hairline pt-3">
          {summary.disclaimer}
        </p>
      </article>
    </div>
  );
}
